import { useState } from "react";

import { useIntersection } from "~/tools/use-intersection";

export interface UseIsStickingOptions {
	/**
	 * The scrolling container HTMLElement to use as the intersection root.
	 */
	scrollingContainer?: null | undefined | HTMLElement;
}

/**
 * React hook that determines if a given DOM node is "sticking" (i.e., not fully visible)
 * within a scrolling container using the Intersection Observer API.
 *
 * @param node - The target HTMLElement to observe, or `null`/`undefined` if not available.
 * @param options - Optional configuration object.
 * @returns `true` as soon as the node sticks
 *
 * @example
 * const isSticking = useIsSticking(ref.current, { scrollingContainer: containerRef.current });
 */
export function useIsSticking(
	node: null | undefined | HTMLElement,
	{ scrollingContainer }: UseIsStickingOptions = {},
): boolean {
	const [isSticking, setIsSticking] = useState(false);

	useIntersection(
		({ intersectionRatio }) => {
			setIsSticking(intersectionRatio < 1);
		},
		{
			node,
			root: scrollingContainer,
			// adjust the margin to trigger the intersection when the node just starts to stick
			rootMargin: "-1px 0px 0px 0px",
			threshold: [1],
		},
	);

	return isSticking;
}
